import { createContext, FC, useContext } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { ConfirmModal, FormInput } from '@microservices/wiskey-react-components'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { Box, Button, Grid, Typography } from '@mui/material'

import { PageContext } from '@pages/ConfigSearchAssistants'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu, useEditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'

import { EditConfigProvider } from '@providers/EditConfigProvider'
import { GeneralButtons } from '@components/GeneralButtons'

import { useSearchAssistantTitleDuplicateValidation } from '@hooks'
import { ENTITY_CONFIG_TYPE, FLEXIBLE, GENERATOR_INPUT_TYPE, REGEX } from '@constants'

import { AddDefaultSettingsDialog } from './components/AddDefaultSettingsDialog/AddDefaultSettingsDialog'
import { AddSearchFilterDialog } from './components/AddSearchFilterDialog'
import { useSearchAssistantCreateOrEdit } from './hooks'
import { SearchAssistantsCreateOrEditContextType } from './types'

export const SearchAssistantsCreateOrEditContext =
  createContext<SearchAssistantsCreateOrEditContextType>(
    {} as SearchAssistantsCreateOrEditContextType
  )

export const SearchAssistantsCreateOrEdit: FC = () => {
  const { t } = useTranslation()

  const {
    configMenuOption,
    handleSelectConfigMenuOption,
    configFileText,
    handleApply,
    handleFileUpload,
    isOpenScriptDialog,
    setIsOpenScriptDialog,
    handleCancel: handleCancelEditConfig,
    handleCopyScript,
  } = useEditConfigMenu(ENTITY_CONFIG_TYPE.SEARCH_ASSISTANTS)

  const { state, data, handlers, mutations } = useSearchAssistantCreateOrEdit()

  const { allSearchAssistants } = useContext(PageContext)

  const {
    showDeleteModal,
    templateTable,
    editCriteria,
    page,
    currentSort,
    showAddSearchFilterModal,
    showDefaultSetting,
    isEditMode,
    isLoadingUpdateVisibility,
    dirtyInternalId,
    isLoadingSearchAssistant,
    isCriteriaLoading,
  } = state

  const {
    objects,
    isLoading,
    loadingData,
    methods,
    objectsValues,
    columns,
    searchAssistantDataById,
    searchAssistantID,
    modelType,
    countPage,
    isEdit,
    isDirty,
    loadingSearchAssistant,
    loadingAllObjects,
    watchInternalId,
    templateData,
  } = data

  const {
    handleOpenAddSearchFilterModal,
    handleCloseAddSearchFilterModal,
    handleCloseDefaultSettings,
    handleDeleteCriteria,
    handlePage,
    handleSetSorting,
    handleVisibility,
    handleChangeObjectCode,
    handleSetCriteriaTable,
    handleNew,
    handleClose,
    handleDelete,
    handleChangeModelSort,
    handleShowDefaultSettings,
    handleSave,
    handleCancel,
  } = handlers

  const { createOrUpdateCriteria } = mutations

  const contextValue = {
    showAddSearchFilterModal,
    showDefaultSetting,
    openAddSearchFilterModal: handleOpenAddSearchFilterModal,
    closeAddSearchFilterModal: handleCloseAddSearchFilterModal,
    closeDefaultSettings: handleCloseDefaultSettings,
    objects,
    templates: templateTable,
    createOrUpdateCriteria,
    isCriteriaLoading,
    onDelete: handleDeleteCriteria,
    editCriteria,
    isLoadingRow: isLoading,
    countPage: 1,
    onChangePage: handlePage,
    currentPage: page,
    currentSort,
    onSetSorting: handleSetSorting,
    setCriteriaTable: handleSetCriteriaTable,
    onUpdateVisibility: handleVisibility,
    loadingData,
    onChangeObjectCode: handleChangeObjectCode,
    searchAssistantId: searchAssistantDataById?.id,
    contextId: searchAssistantID,
    templateData,
  }

  const titleValidation = useSearchAssistantTitleDuplicateValidation({
    allSearchAssistants,
    defaultValue: methods.formState.defaultValues?.title ?? '',
  })

  return (
    <SearchAssistantsCreateOrEditContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.SEARCH_ASSISTANTS}>
        <PageContentLayout>
          <EditConfigScriptDialog />
          <Box sx={{ ...FLEXIBLE, width: '100%' }}>
            <FormProvider {...methods}>
              <Box p={1} pb={0} sx={FLEXIBLE}>
                <Grid
                  container
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  pb={2}
                  py={0.5}
                >
                  <Typography variant='h5'>{t(`searchAssistants.title.${modelType}`)}</Typography>
                  {searchAssistantID && <EditConfigMenu configId={searchAssistantID} />}
                </Grid>
                <FormInput
                  disabled={isEdit && Boolean(watchInternalId)}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  label={t(`searchAssistants.label.internalId`)}
                  loading={loadingSearchAssistant}
                  name='internalId'
                  placeholder={t(`searchAssistants.placeholder.internalId`)}
                  replacePattern={REGEX.MODEL_CODE_REPLACE_PATTERN}
                  size='small'
                  sx={{ pt: 1 }}
                  rules={{
                    required: true,
                    validate: value => {
                      if (value.trim().length === 0) {
                        return false
                      }
                      if (
                        dirtyInternalId &&
                        allSearchAssistants &&
                        allSearchAssistants?.find(sa => sa.internalId === value)
                      ) {
                        return `${value} ${i18next.t('searchAssistants.error.alreadyUsed')}`
                      }
                    },
                  }}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  label={t(`searchAssistants.label.title`)}
                  loading={loadingSearchAssistant}
                  name='title'
                  placeholder={t(`searchAssistants.placeholder.title`)}
                  size='small'
                  sx={{ pt: 1 }}
                  rules={{
                    required: true,
                    validate: value => {
                      if (value.trim().length === 0) {
                        return false
                      }

                      return titleValidation.validate(value, isEdit ? 'edit' : 'create')
                    },
                  }}
                />

                <Grid container justifyContent='flex-end' mb={3}>
                  <GeneralButtons
                    cancelText={t('styleSettings.cancelButton')}
                    isLoading={isLoadingSearchAssistant}
                    saveDisabled={!isDirty || isLoadingSearchAssistant}
                    saveText={t('styleSettings.saveButton')}
                    onCancel={handleCancel}
                    onSave={handleSave}
                  />
                </Grid>

                {showAddSearchFilterModal && <AddSearchFilterDialog isEdit={isEditMode} />}
                {showDefaultSetting && <AddDefaultSettingsDialog />}

                <Grid container mb={1} spacing={1}>
                  <Grid item flex={1}>
                    <FormProvider {...methods}>
                      <FormInput
                        autocompleteOptions={objectsValues}
                        inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                        label={t('label.objectsField')}
                        labelSx={{ mr: 1, width: 130 }}
                        loading={loadingAllObjects}
                        name='objectFilter'
                        placeholder={t('placeholder.objectsField')}
                        size='small'
                      />
                    </FormProvider>
                  </Grid>
                  <Grid item>
                    <Grid container direction={'column'} spacing={1}>
                      <Grid item>
                        <Button
                          fullWidth
                          disabled={!isEdit}
                          variant='outlined'
                          onClick={handleShowDefaultSettings}
                        >
                          {t('searchAssistant.defaultSetting.button')}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          fullWidth
                          disabled={!isEdit}
                          variant={'contained'}
                          onClick={handleNew}
                        >
                          {t('searchAssistant.addBtnText')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {showDeleteModal && (
                  <ConfirmModal
                    actionBtnText={t('modal.delete.btn')}
                    isShow={showDeleteModal}
                    text={t('modal.delete.warning')}
                    title={t('modal.delete.title')}
                    containerStyle={{
                      px: 0.5,
                      py: 0.5,
                      // borderRadius: 0
                    }}
                    onClose={handleClose}
                    onConfirm={handleDelete}
                  />
                )}
                <Grid container sx={FLEXIBLE}>
                  <DataGrid
                    disableMultipleColumnsSorting
                    hideFooter
                    visibility
                    columns={columns}
                    disableColumnResize={false}
                    loading={loadingData}
                    pageCount={countPage}
                    rows={templateTable}
                    sortModel={currentSort}
                    sortingMode={'server'}
                    // sx={{ borderRadius: 0 }}
                    onDisableVisibilityCheckbox={() => isLoadingUpdateVisibility}
                    onSortModelChange={handleChangeModelSort}
                    onVisibility={handleVisibility}
                  />
                </Grid>
              </Box>
            </FormProvider>
          </Box>
        </PageContentLayout>
      </EditConfigProvider>
    </SearchAssistantsCreateOrEditContext.Provider>
  )
}
